import React from 'react';

interface ConnectionCardProps {
  platform: 'Facebook' | 'Instagram' | 'TikTok';
  connected: boolean;
  onConnect: () => void;
  onDisconnect?: () => void;
  data?: {
    pageName?: string;
    displayName?: string;
    username?: string;
    accountType?: string;
    followersCount?: number | null;
    profilePictureUrl?: string | null;
  };
  error?: {
    type: string;
    message: string;
    troubleshootingSteps?: string[];
    troubleshootingUrl?: string;
  };
  canRetry?: boolean;
  disabled?: boolean;
  loading?: boolean;
  disconnecting?: boolean;
}

export const ConnectionCard: React.FC<ConnectionCardProps> = ({
  platform,
  connected,
  onConnect,
  onDisconnect,
  data,
  error,
  canRetry = true,
  disabled = false,
  loading = false,
  disconnecting = false,
}) => {
  const platformColors = {
    Facebook: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-200',
      button: 'bg-blue-600 hover:bg-blue-700',
      shortLabel: 'f',
    },
    Instagram: {
      bg: 'bg-pink-100',
      text: 'text-pink-800',
      border: 'border-pink-200',
      button: 'bg-pink-600 hover:bg-pink-700',
      shortLabel: 'IG',
    },
    TikTok: {
      bg: 'bg-gray-900',
      text: 'text-white',
      border: 'border-gray-300',
      button: 'bg-gray-900 hover:bg-black',
      shortLabel: 'TT',
    },
  };

  const colors = platformColors[platform];

  return (
    <div className={`border rounded-lg p-6 mb-4 ${connected ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div
            className={`w-14 h-14 rounded-full flex items-center justify-center ${
              connected ? 'bg-green-100' : colors.bg
            }`}
          >
            {connected ? (
              <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <span className={`text-2xl font-bold ${colors.text}`}>
                {colors.shortLabel}
              </span>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-lg text-gray-900">{platform}</h3>
            {connected && data ? (
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-600">
                  {data.pageName || data.displayName || data.username}
                </p>
                {data.accountType && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-sm text-xs font-medium">
                    {data.accountType}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Non connesso</p>
            )}
            {data?.followersCount !== undefined && data.followersCount !== null && (
              <p className="text-xs text-gray-500 mt-1">
                {data.followersCount.toLocaleString()} follower
              </p>
            )}
          </div>
        </div>

        {!connected && (
          <button
            onClick={onConnect}
            disabled={disabled || loading}
            className={`px-6 py-2.5 text-white rounded-lg font-medium transition-colors ${
              disabled || loading
                ? 'bg-gray-400 cursor-not-allowed'
                : colors.button
            }`}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Connessione...
              </span>
            ) : (
              `Connetti ${platform}`
            )}
          </button>
        )}

        {connected && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-green-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">Connesso</span>
            </div>
            {onDisconnect && (
              <button
                onClick={onDisconnect}
                disabled={disconnecting}
                className="px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                title="Disconnetti account"
              >
                {disconnecting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Disconnessione...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Disconnetti
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-yellow-800 font-medium mb-2">{error.message}</p>
              {error.troubleshootingSteps && error.troubleshootingSteps.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-yellow-700 font-medium mb-2">Come risolvere:</p>
                  <ol className="list-decimal list-inside text-xs text-yellow-700 space-y-1">
                    {error.troubleshootingSteps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
              {error.troubleshootingUrl && (
                <a
                  href={error.troubleshootingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-3 text-xs text-yellow-800 underline hover:text-yellow-900"
                >
                  Guida completa
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}
              {canRetry && (
                <button
                  onClick={onConnect}
                  disabled={loading}
                  className="mt-3 text-xs text-yellow-800 underline hover:text-yellow-900 font-medium"
                >
                  Riprova
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
